import productApi from '@/modules/projectA/api/product/index';

/** 物料类目一至四级 ID 字段（与后端约定一致） */
export const MATERIAL_CATEGORY_LEVEL_KEYS = [
  'firstCategoryId',
  'secondCategoryId',
  'thirdCategoryId',
  'fourthCategoryId',
];

const LEGACY_MATERIAL_CATEGORY_LEVEL_KEY_MAP = {
  materialCategoryIdLevel1: 'firstCategoryId',
  materialCategoryIdLevel2: 'secondCategoryId',
  materialCategoryIdLevel3: 'thirdCategoryId',
  materialCategoryIdLevel4: 'fourthCategoryId',
};

/** categoryLevel → 对应字段：1→first … 4→fourth */
export const CATEGORY_LEVEL_TO_KEY = {
  1: 'firstCategoryId',
  2: 'secondCategoryId',
  3: 'thirdCategoryId',
  4: 'fourthCategoryId',
};

/** 一级虚拟根节点 categoryId（仅展开，不写入业务 ID） */
export const VIRTUAL_FIRST_CATEGORY_ID = -1;

const EMPTY_PICK_PATH = [undefined, undefined, undefined, undefined];

export const isValidCategoryId = (id) =>
  id !== undefined && id !== null && String(id).trim() !== '';

/** 二至四级及非 -1 的一级视为有效业务 ID */
export const isRealCategoryId = (id) =>
  isValidCategoryId(id) && Number(id) !== VIRTUAL_FIRST_CATEGORY_ID;

export const isApiSuccess = (res) => {
  if (!res) {
    return false;
  }
  if (res.success === true) {
    return true;
  }
  return String(res.code) === '200' || Number(res.code) === 200;
};

const readRouteField = (data, names) => {
  if (!data || typeof data !== 'object') {
    return undefined;
  }
  const keys = Object.keys(data);
  for (let i = 0; i < names.length; i += 1) {
    const target = String(names[i]).toLowerCase();
    const hit = keys.find((k) => String(k).toLowerCase() === target);
    if (hit && data[hit] !== undefined && data[hit] !== null) {
      return data[hit];
    }
  }
  return undefined;
};

const categoryLevelToKey = (level) => CATEGORY_LEVEL_TO_KEY[Number(level)];

/**
 * 按节点 categoryLevel：level N 的 categoryId 写入第 N 级字段
 * @param {Array<object>} nodes 树路径 / routeList 节点
 */
export const levelIdsFromNodesByCategoryLevel = (nodes) => {
  const out = {};
  (nodes || []).forEach((node) => {
    if (!node || !isValidCategoryId(node.categoryId)) {
      return;
    }
    const key = categoryLevelToKey(node.categoryLevel);
    if (key) {
      out[key] = node.categoryId;
    }
  });
  return out;
};

/** 由逐次点击累积的 [一级, 二级, 三级, 四级] 槽位生成各级 ID */
export const levelIdsFromPickPath = (pickPath) => {
  const out = {};
  const slots = Array.isArray(pickPath) ? pickPath : [];
  MATERIAL_CATEGORY_LEVEL_KEYS.forEach((key, index) => {
    if (isValidCategoryId(slots[index])) {
      out[key] = slots[index];
    }
  });
  return out;
};

/** 合并：primary（用户点击 / 已选类目）优先，fallback（接口 / 树路径）仅补空缺 */
export const mergeLevelIds = (primary, fallback) => {
  const out = {};
  MATERIAL_CATEGORY_LEVEL_KEYS.forEach((key) => {
    if (isValidCategoryId(primary && primary[key])) {
      out[key] = primary[key];
    } else if (isValidCategoryId(fallback && fallback[key])) {
      out[key] = fallback[key];
    }
  });
  return out;
};

/**
 * 根据 selectCategoryRouteById 接口 data 组装一至四级 ID
 */
export const levelIdsFromCategoryRouteApi = (data, leafCategoryId) => {
  if (!data || typeof data !== 'object') {
    return {};
  }

  const routeList = data.routeList || data.categoryRouteList || data.route;
  if (Array.isArray(routeList) && routeList.length > 0) {
    const fromRoute = levelIdsFromNodesByCategoryLevel(routeList);
    if (Object.keys(fromRoute).length > 0) {
      return fromRoute;
    }
  }

  const out = {};
  const first = readRouteField(data, ['firstcategoryid', 'firstCategoryId']);
  const second = readRouteField(data, ['secondcategoryid', 'secondCategoryId']);
  const third = readRouteField(data, ['thirdcategoryid', 'thirdCategoryId']);
  const fourth = readRouteField(data, ['fourthcategoryid', 'fourthCategoryId']);

  if (isValidCategoryId(first)) {
    out.firstCategoryId = first;
  }
  if (isValidCategoryId(second)) {
    out.secondCategoryId = second;
  }
  if (isValidCategoryId(third)) {
    out.thirdCategoryId = third;
  }
  if (isValidCategoryId(fourth)) {
    out.fourthCategoryId = fourth;
  }

  if (Object.keys(out).length > 0) {
    return out;
  }

  if (isValidCategoryId(leafCategoryId) && data.categoryLevel != null) {
    const key = categoryLevelToKey(data.categoryLevel);
    if (key) {
      return { [key]: leafCategoryId };
    }
  }

  return out;
};

/**
 * 从树根到当前节点的路径解析（优先各节点 categoryLevel）
 */
export const levelIdsFromTreeNodePath = (pathNodes, leafCategoryId) => {
  const fromPath = levelIdsFromNodesByCategoryLevel(pathNodes);
  if (isValidCategoryId(leafCategoryId)) {
    const leafNode = (pathNodes || []).find(
      (n) => n && Number(n.categoryId) === Number(leafCategoryId)
    );
    if (leafNode && leafNode.categoryLevel != null) {
      const key = categoryLevelToKey(leafNode.categoryLevel);
      if (key) {
        return { ...fromPath, [key]: leafCategoryId };
      }
    }
    if (!Object.keys(fromPath).length) {
      return { fourthCategoryId: leafCategoryId };
    }
  }
  return fromPath;
};

export const levelIdsFromCategoryRouteList = (routeList, leafCategoryId) =>
  levelIdsFromTreeNodePath(routeList, leafCategoryId);

/** @deprecated 无 categoryLevel 时按路径顺序兜底 */
export const mapPathCategoryIdsToLevelSlots = (pathCategoryIds) => {
  const ids = (pathCategoryIds || []).filter((id) => isRealCategoryId(id));
  const nodes = ids.map((categoryId, index) => ({
    categoryId,
    categoryLevel: Math.min(index + 2, 4),
  }));
  return levelIdsFromNodesByCategoryLevel(nodes);
};

/** 叶子节点 isLeaf：1 为叶子，0 为非叶子 */
export const isMaterialCategoryLeafNode = (item) =>
  Number(item && item.isLeaf) === 1;

/**
 * 叶子节点调 selectCategoryRouteById，返回的各级 ID 直接替换本地推算值
 */
export const fetchCategoryRouteById = async (categoryId) => {
  if (!isValidCategoryId(categoryId)) {
    return {};
  }
  try {
    const res = await productApi.selectCategoryRouteById({ categoryId });
    if (isApiSuccess(res) && res.data) {
      return levelIdsFromCategoryRouteApi(res.data, categoryId);
    }
  } catch (e) {
    // 接口失败时由调用方兜底
  }
  return {};
};

/**
 * 按末级 categoryId 调 selectCategoryRouteById；接口成功则直接采用返回的各级 ID
 * @param {Array<number|string|undefined>} pickPath 懒加载树下逐次点击的 [一级,二级,三级,四级]
 */
export const fetchMaterialCategoryLevelIds = async (categoryId, pathNodes, pickPath) => {
  const fromApi = await fetchCategoryRouteById(categoryId);
  if (Object.keys(fromApi).length > 0) {
    return fromApi;
  }
  const fromClicks = levelIdsFromPickPath(pickPath);
  const fromPath = levelIdsFromTreeNodePath(pathNodes, categoryId);
  return mergeLevelIds(fromClicks, fromPath);
};

/** 末级类目已选：对应 categoryLevel 槽位已有 ID */
export const hasCompleteMaterialCategoryPickPath = (pickPath, leafCategoryLevel) => {
  const ids = levelIdsFromPickPath(pickPath);
  const level = Number(leafCategoryLevel);
  if (level >= 1 && level <= 4) {
    const key = categoryLevelToKey(level);
    return key ? isValidCategoryId(ids[key]) : false;
  }
  return isValidCategoryId(ids.fourthCategoryId);
};

/** 树根虚拟节点（categoryId=-1），仅展开不选中 */
export const isVirtualFirstCategoryNode = (categoryId) => Number(categoryId) === VIRTUAL_FIRST_CATEGORY_ID;

/**
 * 树点击：按 item.categoryLevel 写入对应字段，并清空更深层级
 * @returns {object} 当前各级 ID
 */
export const assignCategoryIdFromTreeClick = (state, node, categoryId, item) => {
  if (isVirtualFirstCategoryNode(categoryId)) {
    resetMaterialCategoryPickPath(state);
    return levelIdsFromPickPath(state.materialCategoryPickPath);
  }

  if (!isValidCategoryId(categoryId)) {
    return levelIdsFromPickPath(state.materialCategoryPickPath);
  }

  const level = Number(item && item.categoryLevel);
  if (!level || level < 1 || level > 4) {
    return levelIdsFromPickPath(state.materialCategoryPickPath);
  }

  const slots = Array.isArray(state.materialCategoryPickPath)
    ? [...state.materialCategoryPickPath]
    : [...EMPTY_PICK_PATH];

  while (slots.length < 4) {
    slots.push(undefined);
  }

  slots[level - 1] = categoryId;
  for (let i = level; i < 4; i += 1) {
    slots[i] = undefined;
  }

  state.materialCategoryPickPath = slots;
  return levelIdsFromPickPath(slots);
};

export const resetMaterialCategoryPickPath = (state) => {
  state.materialCategoryPickPath = [...EMPTY_PICK_PATH];
  state.pickAnchorLevel = null;
};

export const syncMaterialCategoryPickPathFromCategory = (state, categoryItem) => {
  if (!categoryItem || typeof categoryItem !== 'object') {
    resetMaterialCategoryPickPath(state);
    return;
  }
  const picked = pickMaterialCategoryLevelIds(categoryItem);
  state.materialCategoryPickPath = [
    picked.firstCategoryId,
    picked.secondCategoryId,
    picked.thirdCategoryId,
    picked.fourthCategoryId,
  ];
  state.pickAnchorLevel = null;
};

export const pickMaterialCategoryLevelIds = (source) => {
  if (!source || typeof source !== 'object') {
    return {};
  }
  const out = {};
  MATERIAL_CATEGORY_LEVEL_KEYS.forEach((key) => {
    if (source[key] !== undefined && source[key] !== null) {
      out[key] = source[key];
    }
  });
  Object.keys(LEGACY_MATERIAL_CATEGORY_LEVEL_KEY_MAP).forEach((legacyKey) => {
    const newKey = LEGACY_MATERIAL_CATEGORY_LEVEL_KEY_MAP[legacyKey];
    if (out[newKey] === undefined && source[legacyKey] !== undefined && source[legacyKey] !== null) {
      out[newKey] = source[legacyKey];
    }
  });
  return out;
};

/**
 * 提交/保存：优先使用类目对象上已选好的各级 ID；按 categoryLevel 补全末级
 */
export const buildMaterialCategoryLevelIdsForApi = (cat) => {
  if (!cat || typeof cat !== 'object' || cat.categoryId === undefined || cat.categoryId === null) {
    return {};
  }

  const picked = pickMaterialCategoryLevelIds(cat);
  const fromRoute = levelIdsFromNodesByCategoryLevel(cat.routeList);
  const out = mergeLevelIds(picked, fromRoute);

  const level = Number(cat.categoryLevel);
  const key = categoryLevelToKey(level);
  if (key && isValidCategoryId(cat.categoryId)) {
    out[key] = cat.categoryId;
  }

  if (Object.keys(out).length > 0) {
    return out;
  }

  const pathIds = [];
  [picked.firstCategoryId, picked.secondCategoryId, picked.thirdCategoryId, cat.parentId, cat.categoryId].forEach(
    (id) => {
      if (isRealCategoryId(id) && !pathIds.includes(id)) {
        pathIds.push(id);
      }
    }
  );

  return mergeLevelIds(out, mapPathCategoryIdsToLevelSlots(pathIds));
};

export const applyMaterialCategoryLevelIdsToPayload = (productRow, target) => {
  if (!productRow || typeof productRow !== 'object' || !target || typeof target !== 'object') {
    return;
  }
  const cat =
    Array.isArray(productRow.certificateMaterialCategory) && productRow.certificateMaterialCategory[0];
  const source = cat || productRow;
  const picked = pickMaterialCategoryLevelIds(source);
  const rebuilt = buildMaterialCategoryLevelIdsForApi(source);
  const ids = mergeLevelIds(picked, rebuilt);

  MATERIAL_CATEGORY_LEVEL_KEYS.forEach((key) => {
    if (ids[key] !== undefined) {
      target[key] = ids[key];
    }
  });
};

export const mergeCategoryWithLevelIds = (category, levelIds) => ({
  ...category,
  ...levelIds,
});
